import type { ImgHTMLAttributes } from "react";
import type { StorefrontResponsiveImage } from "@emynor/storefront-sdk";
import type { ResponsiveImageSource } from "@/lib/types";

type Props = Omit<ImgHTMLAttributes<HTMLImageElement>, "src" | "srcSet" | "sizes" | "width" | "height"> & {
  source: ResponsiveImageSource;
  sizes: string;
  width?: number;
  height?: number;
};

export function responsiveSrcSet(source: ResponsiveImageSource): string | undefined {
  if (typeof source === "string") return undefined;
  const byWidth = new Map<number, StorefrontResponsiveImage["variants"][number]>();
  for (const variant of source.variants) {
    if (!byWidth.has(variant.width)) byWidth.set(variant.width, variant);
  }
  const unique = [...byWidth.values()].sort((left, right) => left.width - right.width);
  return unique.length ? unique.map((variant) => `${variant.url} ${variant.width}w`).join(", ") : undefined;
}

export function ResponsiveImage({ source, sizes, width, height, decoding = "async", ...props }: Props) {
  const descriptor = typeof source === "string" ? undefined : source;
  const srcSet = responsiveSrcSet(source);
  return (
    <img
      {...props}
      src={descriptor?.src ?? (source as string)}
      {...(srcSet ? { srcSet, sizes } : {})}
      width={width ?? descriptor?.width}
      height={height ?? descriptor?.height}
      decoding={decoding}
    />
  );
}
